/**
 * Commissioni mostrate accanto a quelle applicate: le **maggiorate** barrate e
 * la tariffa del piano.
 *
 * Fino a ieri erano due numeri scritti qui: le maggiorate (12% / 9% / 6,5%,
 * clausola 3.2.6) e — sulla pagina prezzi — le ordinarie. Ora entrambe si
 * modificano dal pannello **Tariffe** dell'area staff, quindi leggerle da qui
 * significherebbe pubblicare una cifra che il checkout non applica piu': il
 * costo di quell'errore e' una contestazione, non una svista grafica.
 *
 * La verita' sta in `GET /api/billing/plans` (`plans[].commission_pct` = tariffa
 * del piano, `increased_rates` = maggiorate). Questo modulo tiene i valori di
 * fallback — servono quando la richiesta non arriva, e sono gli stessi che il
 * backend usa senza configurazione — e la cache di una sola richiesta: le
 * pagine che mostrano le percentuali sono tre, e non devono farne tre.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";

// base = piano Free (nessun abbonamento), enterprise comprende la tariffa
// dedicata alle catene. `corporate` non e' in promozione.
export const LIST_COMMISSION_PCT = { base: 12, pro: 9, enterprise: 6.5 };

//: Tariffe ordinarie di default, le stesse di `backend/core.py`.
export const APPLIED_COMMISSION_PCT = { base: 8, pro: 6, enterprise: 4.5, corporate: 4 };

/** "4.5" -> "4,5%": la percentuale come la scrive un italiano. */
export const pctIt = (n) => `${String(Number(n)).replace(".", ",")}%`;

/**
 * Prezzo di listino barrato per il piano, oppure `null` se non c'e' promo.
 *
 * `overrides` sono le maggiorate configurate nel pannello (formato
 * `{ base: 12, pro: 9, enterprise: 6.5 }`): se ci sono, vincono sul fallback.
 */
export const listCommissionPct = (tier, overrides) => {
  const v = (overrides || {})[String(tier || "").toLowerCase()]
    ?? LIST_COMMISSION_PCT[String(tier || "").toLowerCase()];
  return typeof v === "number" ? v : null;
};

/** Tariffa del piano applicata oggi, con lo stesso meccanismo di override. */
export const appliedCommissionPct = (tier, overrides) => {
  const v = (overrides || {})[String(tier || "").toLowerCase()]
    ?? APPLIED_COMMISSION_PCT[String(tier || "").toLowerCase()];
  return typeof v === "number" ? v : null;
};

let _cache = null;
let _inflight = null;

/**
 * Azzera la cache (solo per i test).
 *
 * La cache e' di modulo: in un test che monta la pagina due volte con tariffe
 * diverse la seconda vedrebbe quelle della prima — verde o rosso a seconda
 * dell'ordine di esecuzione, che e' la peggior forma di test rotto. Stessa
 * scelta di `city_index.reset_cache()` nel backend.
 */
export function resetCommissionRatesCache() {
  _cache = null;
  _inflight = null;
}

function fetchRates() {
  if (_cache) return Promise.resolve(_cache);
  if (!_inflight) {
    _inflight = api.get("/billing/plans")
      .then((r) => {
        const d = (r && r.data) || {};
        const ordinary = {};
        const ocrIncludedScans = {};
        (d.plans || []).forEach((p) => {
          if (p && p.tier != null && typeof p.commission_pct === "number") {
            ordinary[p.tier] = p.commission_pct;
          }
          if (p && p.tier != null && typeof p.ocr_included_scans_monthly === "number") {
            ocrIncludedScans[p.tier] = p.ocr_included_scans_monthly;
          }
          (p?.variants || []).forEach((v) => {
            if (typeof v.ocr_included_scans_monthly === "number") {
              ocrIncludedScans[`${p.tier}_${v.code}`] = v.ocr_included_scans_monthly;
            }
          });
        });
        _cache = { increased: d.increased_rates || {}, ordinary, ocrIncludedScans };
        return _cache;
      })
      .catch(() => {
        // Rete assente o backend giu': si resta sui default e la pagina si
        // disegna lo stesso. Nessuna eccezione in console per una cifra.
        _inflight = null;
        return null;
      });
  }
  return _inflight;
}

/**
 * Percentuali configurate: `{ increased: {...}, ordinary: {...} }`.
 *
 * Restituisce oggetti vuoti finche' la risposta non e' arrivata — i chiamanti
 * passano il risultato come `overrides`, quindi "vuoto" significa "usa i
 * default", non "zero".
 */
export function useCommissionRates() {
  const [rates, setRates] = useState(_cache);
  useEffect(() => {
    let alive = true;
    if (_cache) {
      setRates(_cache);
      return () => { alive = false; };
    }
    fetchRates().then((r) => { if (alive && r) setRates(r); });
    return () => { alive = false; };
  }, []);
  return rates || { increased: {}, ordinary: {}, ocrIncludedScans: {} };
}
